import React, { useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import PageTitle from '../../components/PageTitle';
import SearchCard from '../../components/SearchCard';
import { AnimatePresence } from 'framer-motion';
import { getEffectiveRoleForSorting, getRankScore as getRankScoreFromUtils } from '../../utils/rank';
import api from '../../services/api';

interface MemberDetails {
  photo: string;
  fullName: string;
  permanentEnrollmentNumber: string;
  memberType: string;
  rank: string;
  designation: string;
  branch: string;
  memberStatus: string;
  languagesKnown: string;
  dateOfBirth: string;
  currentCity: string;
  currentDistrict: string;
  currentState: string;
  currentPinCode: string;
  bloodGroup: string;
}

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [memberDetails, setMemberDetails] = useState<MemberDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getRankScore = (rank: string, designation: string) => {
    return getRankScoreFromUtils(getEffectiveRoleForSorting(rank, designation));
  };

  const getMemberTypeScore = (value: string) => {
    const v = (value || '').trim().toLowerCase();
    if (!v) return 999;

    if (v === 'national') return 0;

    // Treat any specific zone as "Zone"
    if (v === 'zone' || v.endsWith(' zone')) return 1;

    if (v === 'state') return 2;
    if (v === 'district') return 3;
    if (v === 'city') return 4;
    if (v === 'woman' || v === 'women') return 5;

    return 999;
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMemberDetails([]);

    try {
      // Fetch all active members from Cloudflare API
      const response = await api.getPublicMembers(undefined, 'Active');
      const members = response.members || [];
      
      const searchTerm = searchQuery.trim().toLowerCase();
      
      const searchResults = members.filter((data: any) => {
        const fullName = (data.full_name || '').toLowerCase();
        const enrollmentNumber = data.enrollment_number || '';
        
        return fullName.includes(searchTerm) || enrollmentNumber.toLowerCase() === searchTerm;
      });

      if (searchResults.length === 0) {
        setError('No member found with the provided details.');
        return;
      }

      // Sort results
      const sortedResults = [...searchResults].sort((a: any, b: any) => {
        const aTypeScore = getMemberTypeScore(a.member_type || '');
        const bTypeScore = getMemberTypeScore(b.member_type || '');
        if (aTypeScore !== bTypeScore) return aTypeScore - bTypeScore;

        const aRankScore = getRankScore(a.rank || '', a.designation || '');
        const bRankScore = getRankScore(b.rank || '', b.designation || '');

        if (aRankScore !== bRankScore) return aRankScore - bRankScore;

        const aName = (a.full_name || '').toString().toLowerCase();
        const bName = (b.full_name || '').toString().toLowerCase();
        return aName.localeCompare(bName);
      });

      const allMembers: MemberDetails[] = sortedResults.map((data: any) => ({
        photo: data.photo || '',
        fullName: data.full_name || '',
        permanentEnrollmentNumber: data.enrollment_number || '',
        memberType: data.member_type || '',
        rank: data.rank || '',
        designation: data.designation || '',
        branch: data.branch || '',
        memberStatus: data.member_status || '',
        languagesKnown: data.languages_known || '',
        dateOfBirth: data.date_of_birth || '',
        currentCity: data.current_city_village || '',
        currentDistrict: data.current_district || '',
        currentState: data.current_state || '',
        currentPinCode: data.current_pin_code || '',
        bloodGroup: data.blood_group || ''
      }));
      
      setMemberDetails(allMembers);
    } catch (error) {
      console.error('Error fetching member details:', error);
      setError('An error occurred while fetching member details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <PageTitle
        title="Search Member"
        subtitle="Search by Name or Enrollment Number"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Form */}
        <div className="max-w-3xl mx-auto mb-12">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-100" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter name or enrollment number"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-600 rounded-lg bg-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2" />
                  Searching...
                </div>
              ) : (
                'Search'
              )}
            </button>
          </form>
        </div>

        {/* Results Section */}
        <div className="max-w-4xl mx-auto">
          {error && (
            <div className="text-red-500 text-center py-4 bg-red-500/10 rounded-xl backdrop-blur-sm">
              {error}
            </div>
          )}

          <AnimatePresence mode="wait">
            {memberDetails.length > 0 && (
              <div className="space-y-6">
                {memberDetails.map((member, index) => (
                  <SearchCard key={index} member={member} />
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Search;
